import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faPlus,
  faFilter,
  faCalendarAlt,
  faDownload,
  faBook // Added book icon for "Move to Resources"
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../../components/config/apiConfig';
import axios from 'axios';

function ProgramsList() {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    type: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  
  useEffect(() => {
    fetchPrograms();
  }, [currentPage, filter]);
  
  const fetchPrograms = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      let url = `${ADMIN_URLS.PROGRAMS}?page=${currentPage}`;
      
      if (filter.type) {
        url += `&type=${filter.type}`;
      }
      
      if (filter.status) {
        url += `&status=${filter.status}`;
      }
      
      if (searchTerm) {
        url += `&search=${searchTerm}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      setPrograms(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching programs:', error);
      setErrorMessage('Failed to fetch programs. Please try again later.');
      setIsLoading(false);
      setPrograms([]);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchPrograms();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) {
      return;
    }
    
    try {
      await axios.delete(`${ADMIN_URLS.PROGRAMS}/${programId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      // Refresh the programs list after deletion
      fetchPrograms();
    } catch (error) {
      console.error('Error deleting program:', error);
      setErrorMessage('Failed to delete program. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      let url = `${ADMIN_URLS.PROGRAMS}/export?`;
      
      if (filter.type) {
        url += `&type=${filter.type}`;
      }
      
      if (filter.status) {
        url += `&status=${filter.status}`;
      }
      
      if (searchTerm) {
        url += `&search=${searchTerm}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a download link and trigger it
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `programs_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting programs:', error);
      setErrorMessage('Failed to export programs. Please try again later.');
      setIsExporting(false);
    }
  };

  const handleMoveToResources = async (programId) => {
    if (!window.confirm('Are you sure you want to move this program to resources?')) {
      return;
    }
    
    try {
      await axios.post(`${ADMIN_URLS.PROGRAMS}/${programId}/convert-to-resource`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      // Show success message or update the program status
      setErrorMessage('');
      alert('Program successfully moved to resources!');
      
      // Refresh the programs list
      fetchPrograms();
    } catch (error) {
      console.error('Error moving program to resources:', error);
      setErrorMessage('Failed to move program to resources. Please try again.');
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Programs</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Programs</span>
          </li>
        </ul>
      </div>
      
      {errorMessage && (
        <div className="admin-alert admin-alert-danger" style={{ marginBottom: '1rem' }}>
          {errorMessage}
        </div>
      )}
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search programs..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="type"
                value={filter.type}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Types</option>
                <option value="workshop">Workshop</option>
                <option value="webinar">Webinar</option>
                <option value="course">Course</option>
                <option value="conference">Conference</option>
              </select>
              
              <select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              
              <button
                className="admin-btn admin-btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
                onClick={handleExportCSV}
                disabled={isExporting}
              >
                <FontAwesomeIcon icon={faDownload} />
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </button>
              
              <Link to="/admin/programs/add" className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
                <FontAwesomeIcon icon={faPlus} />
                Add Program
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Program Name</th>
                <th>Type</th>
                <th>Date</th>
                <th>Speaker</th>
                <th>Location</th>
                <th>Status</th>
                <th>Participants</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.length > 0 ? (
                programs.map((program) => (
                  <tr key={program.id}>
                    <td>{program.id}</td>
                    <td>{program.title}</td>
                    <td style={{ textTransform: 'capitalize' }}>{program.type}</td>
                    <td>{formatDate(program.program_date)} - <br></br>{formatDate(program.end_date)}</td>
                    <td>{program.speaker_name || 'N/A'}</td>
                    <td>{program.location}</td>
                    <td>
                      <span 
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          backgroundColor: 
                            program.status === 'upcoming' ? '#cce5ff' : 
                            program.status === 'ongoing' ? '#d4edda' : 
                            program.status === 'completed' ? '#d1ecf1' : 
                            '#f8d7da',
                          color: 
                            program.status === 'upcoming' ? '#004085' : 
                            program.status === 'ongoing' ? '#155724' : 
                            program.status === 'completed' ? '#0c5460' : 
                            '#721c24'
                        }}
                      >
                        {program.status}
                      </span>
                    </td>
                    <td>{program.participants}</td>
                    <td>
                      <div className="admin-table-actions">
                        <Link 
                          to={`/admin/programs/${program.id}`} 
                          className="admin-table-btn admin-table-btn-view"
                          title="View Program Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/admin/programs/${program.id}/edit`} 
                          className="admin-table-btn admin-table-btn-edit"
                          title="Edit Program"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        {program.status === 'completed' && (
                          <button 
                            className="admin-table-btn admin-table-btn-success"
                            title="Move to Resources"
                            onClick={() => handleMoveToResources(program.id)}
                          >
                            <FontAwesomeIcon icon={faBook} />
                          </button>
                        )}
                        <button 
                          className="admin-table-btn admin-table-btn-delete"
                          title="Delete Program"
                          onClick={() => handleDeleteProgram(program.id)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                    No programs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '1rem', 
            borderTop: '1px solid var(--admin-border)'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &laquo;
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className="admin-btn"
                  style={{ 
                    backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                    color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                    padding: '0.5rem 0.75rem'
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default ProgramsList;
